/**
 * Super Admin → Feature Catalogue (iter308s)
 *
 * Rewrite to fix two issues reported on the previous version:
 *   1. Each category rendered its own <table> → column widths
 *      auto-sized per-section → Dependencies/Price/Status columns
 *      visibly shifted between BOOKINGS, CAMPS, COMMS, etc.
 *   2. Long page with no way to collapse the categories you don't
 *      care about right now.
 *
 * Approach:
 *   - Single CSS-grid layout shared by every category so column
 *     positions are pixel-identical across the whole page.
 *   - Each category is a collapsible section (`<details>`-style toggle).
 *   - Sticky search input + "expand/collapse all" controls at the top.
 */
import { useEffect, useMemo, useState } from "react";
import { RequiresCell, NeededByCell } from "./FeatureDependencyCells";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  Search,
  ChevronDown,
  ChevronRight,
  ListChecks,
  AlertTriangle,
} from "lucide-react";

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + "/api";
const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Shared grid template so every row in every section aligns identically.
// 6 columns: Feature, Requires, Needed by (all flexible), Price, Status, Save
const GRID = "minmax(220px, 1.1fr) minmax(220px, 1.4fr) minmax(200px, 1.2fr) 110px 120px 80px";

export default function SuperAdminFeatures() {
  const [features, setFeatures] = useState([]);
  const [graphCycles, setGraphCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({}); // category -> boolean
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/super-admin/features/graph`, { headers: authHeader() });
      setFeatures(r.data.features || []);
      setGraphCycles(r.data.cycles || []);
    } catch (e) {
      toast.error("Failed to load features");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const byKey = useMemo(() => Object.fromEntries(features.map((f) => [f.key, f])), [features]);
  const allKeys = useMemo(() => features.map((f) => f.key).sort(), [features]);
  const setEdit = (key, patch) => setEdits((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const saveOne = async (key) => {
    const patch = edits[key];
    if (!patch) return;
    setSavingKey(key);
    try {
      await axios.patch(`${API}/super-admin/features/${key}`, patch, { headers: authHeader() });
      toast.success(`${key} updated`);
      setEdits((s) => { const c = { ...s }; delete c[key]; return c; });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : (d?.errors || []).map((x) => x.message).filter(Boolean).join("; ") || d?.message || "Save failed";
      toast.error(msg);
    } finally {
      setSavingKey(null);
    }
  };

  // Group + filter
  const byCategory = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = features.filter((f) => {
      if (showIssuesOnly && !f.has_dependency_issue) return false;
      if (!q) return true;
      return (
        (f.name || "").toLowerCase().includes(q) ||
        (f.key || "").toLowerCase().includes(q) ||
        (f.description || "").toLowerCase().includes(q) ||
        (f.category || "").toLowerCase().includes(q)
      );
    });
    const map = {};
    filtered.forEach((f) => {
      const c = f.category || "other";
      map[c] = map[c] || [];
      map[c].push(f);
    });
    return map;
  }, [features, query, showIssuesOnly]);

  const categories = Object.keys(byCategory).sort();
  const dirtyCount = Object.keys(edits).length;
  const issueCount = features.filter((f) => f.has_dependency_issue).length;

  const toggle = (cat) => setCollapsed((s) => ({ ...s, [cat]: !s[cat] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => setCollapsed(Object.fromEntries(categories.map((c) => [c, true])));

  return (
    <div className="space-y-4" data-testid="super-admin-features-page">
      {/* Page header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <ListChecks className="w-7 h-7 text-emerald-600" />
            Feature Catalogue
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Set individual feature prices. Bundle savings are computed against the sum. {features.length} feature(s) across {categories.length} categor{categories.length === 1 ? "y" : "ies"}.
          </p>
        </div>
        {dirtyCount > 0 && (
          <span data-testid="features-dirty-badge" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-800 text-xs font-medium">
            {dirtyCount} unsaved change{dirtyCount === 1 ? "" : "s"}
          </span>
        )}
      </header>

      {graphCycles.length > 0 && (
        <div
          data-testid="features-cycle-banner"
          className="border border-rose-300 bg-rose-50 text-rose-800 rounded p-3 text-sm flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">
              Cyclic dependencies detected in catalogue ({graphCycles.length}):
            </div>
            <ul className="mt-1 space-y-0.5 font-mono text-xs">
              {graphCycles.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Sticky controls */}
      <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-slate-50/95 backdrop-blur border-b border-slate-200 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search features by name, key, description…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="features-search-input"
            className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded text-sm"
          />
        </div>
        <button
          type="button"
          onClick={expandAll}
          data-testid="features-expand-all-btn"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-white"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          data-testid="features-collapse-all-btn"
          className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-white"
        >
          Collapse all
        </button>
        <label
          className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-white inline-flex items-center gap-2 cursor-pointer"
          data-testid="features-issues-only-toggle"
        >
          <input
            type="checkbox"
            checked={showIssuesOnly}
            onChange={(e) => setShowIssuesOnly(e.target.checked)}
            className="w-3.5 h-3.5"
          />
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          Issues only
          {issueCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
              {issueCount}
            </span>
          )}
        </label>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm">
          {query ? "No features match your search." : "No features found."}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Shared header row, sticky */}
          <div
            className="grid bg-white border border-slate-200 rounded-lg text-xs text-slate-500 uppercase tracking-wide font-semibold sticky top-[60px] z-[5]"
            style={{ gridTemplateColumns: GRID }}
            data-testid="features-header-row"
          >
            <div className="px-4 py-2">Feature</div>
            <div className="px-4 py-2">Requires (turning this on also turns these on)</div>
            <div className="px-4 py-2">Needed by</div>
            <div className="px-4 py-2">Price ($/mo)</div>
            <div className="px-4 py-2">Status</div>
            <div className="px-4 py-2 text-right">Action</div>
          </div>

          {categories.map((cat) => {
            const feats = byCategory[cat];
            const isCollapsed = !!collapsed[cat];
            return (
              <section
                key={cat}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                data-testid={`feature-category-${cat}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(cat)}
                  data-testid={`feature-category-toggle-${cat}`}
                  className="w-full bg-slate-50 hover:bg-slate-100 px-4 py-2.5 text-left flex items-center gap-2 transition-colors"
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  )}
                  <span className="font-semibold text-sm text-slate-700 uppercase">{cat}</span>
                  <span className="text-xs text-slate-400">({feats.length})</span>
                </button>

                {!isCollapsed && (
                  <div className="divide-y divide-slate-100">
                    {feats.map((f) => {
                      const draft = edits[f.key] || {};
                      const price = draft.price_per_month ?? f.price_per_month ?? "";
                      const status = draft.status ?? f.status ?? "active";
                      const dirty = Object.keys(draft).length > 0;
                      return (
                        <div
                          key={f.key}
                          className="grid items-start"
                          style={{ gridTemplateColumns: GRID }}
                          data-testid={`feature-row-${f.key}`}
                        >
                          <div className="px-4 py-3 min-w-0">
                            <div className="font-medium text-sm flex items-center gap-1.5">
                              {f.name}
                              {f.has_dependency_issue && (
                                <span
                                  title={
                                    f.in_cycle
                                      ? "This feature is part of a dependency cycle"
                                      : `Missing deps: ${(f.missing_deps || []).join(", ")}`
                                  }
                                  data-testid={`feature-issue-badge-${f.key}`}
                                  className="inline-flex items-center"
                                >
                                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 font-mono break-all">{f.key}</div>
                            {f.description && (
                              <div className="text-xs text-slate-400 mt-0.5">{f.description}</div>
                            )}
                          </div>
                          <div className="px-4 py-3 min-w-0">
                            <RequiresCell f={f} deps={draft.depends_on ?? f.depends_on ?? []} byKey={byKey} allKeys={allKeys}
                              missing={f.missing_deps || []} onChange={(v) => setEdit(f.key, { depends_on: v })} />
                          </div>
                          <div className="px-4 py-3 min-w-0">
                            <NeededByCell f={f} />
                          </div>
                          <div className="px-4 py-3">
                            <input
                              data-testid={`feature-price-${f.key}`}
                              type="number"
                              placeholder="TBD"
                              value={price}
                              onChange={(e) =>
                                setEdit(f.key, {
                                  price_per_month: e.target.value === "" ? null : Number(e.target.value),
                                })
                              }
                              className="w-full border border-slate-300 rounded px-2 py-1 text-sm"
                            />
                          </div>
                          <div className="px-4 py-3">
                            <select
                              data-testid={`feature-status-${f.key}`}
                              value={status}
                              onChange={(e) => setEdit(f.key, { status: e.target.value })}
                              className="w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                            >
                              <option value="active">active</option>
                              <option value="archived">archived</option>
                            </select>
                          </div>
                          <div className="px-4 py-3 flex justify-end">
                            {dirty && (
                              <button
                                data-testid={`feature-save-${f.key}`}
                                onClick={() => saveOne(f.key)}
                                disabled={savingKey === f.key}
                                className="bg-slate-900 text-white rounded px-2.5 py-1 text-xs hover:bg-slate-800 inline-flex items-center gap-1 disabled:opacity-50"
                              >
                                {savingKey === f.key ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Save className="w-3 h-3" />
                                )}
                                Save
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
